import { useState, useEffect, useCallback, useRef, useMemo } from "react";

import { ArrowUpDown, Pencil, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import {
  truncateDecimals,
  formatFiatWithTwoDecimals,
} from "../utils/numberFormatter";
import OrderCreationCard from "./OrderCreationCard";
import {
  useCryptoAssets,
  useFetchPairs,
  useTradeRate,
} from "../hooks/query/queries";
import { useNotification } from "../context/NotificationProvider";
import { TradePair } from "./TradePairBalance";
import CurrencyFormatter from "../utils/currencyFormatter";
import { useIsMobile } from "../hooks/use-mobile";
import AssetSelectionDialog from "./AssetSelectionDialog";
import AssetSelectionDropdown from "./AssetSelectionDropdown";
import PairSelectionDialog from "./PairSelectionDialog";
import PairSelectionDropdown from "./PairSelectionDropdown";

interface Asset {
  assetId: string;
  asset: string;
  assetIcon?: string;
  assetName?: string;
  availableAmount?: number;
  availableAmountFormatted?: string;
  valueInLocalCurrency?: string;
  [key: string]: unknown;
}

interface TradingPair {
  id: string;
  pair: string;
  asset: string;
  assetIcon?: string;
  assetName?: string;
  type?: string;
  availableAmount?: number;
  formattedAvailableAmount?: string;
  [key: string]: unknown;
}

interface TradeRate {
  rate: number;
  [key: string]: unknown;
}

interface PairInfo {
  pair: string;
  minBuyAmountTail?:
    | {
        $numberDecimal?: string;
      }
    | number;
  minSellAmountTail?:
    | {
        $numberDecimal?: string;
      }
    | number;
  [key: string]: unknown;
}

interface AssetSelectorWalletProps {
  onDashboard?: boolean;
  onSelect?: (asset: Asset) => void;
  selectedSaleAsset?: Asset | null;
  setSelectedSaleAsset?: (asset: Asset | null) => void;
  setDisabled?: (value: boolean) => void;
  orderAmount?: number | null;
  onChange?: (inAmount: number, outAmount: number) => void;
  setParentRate?: (rate: TradeRate) => void;
  typeOfTrade?: string;
  onTargetCancel?: () => void;
  pair?: TradingPair | null;
  onTarget?: (amount: number) => void;
  onReject?: () => void;
  onAccept?: () => void;
  hotActionAsset?: Asset | null;
  tradingAssets?: Asset[];
  setSelectedAsset?: (asset: Asset | null) => void;
  selectedAsset?: Asset | null;
  amount?: string | number | null;
  setAmount?: (value: number | string) => void;
  isPostTrade?: boolean;
  currentPairs?: TradingPair[] | null;
  onPairSelect?: (pair: TradingPair) => void;
  zarBalance: number;
  fiatBalances: {
    asset: string;
    valueInLocalCurrency: string;
  }[];
  onOrderViewChange?: (value: boolean) => void;
  onOrderCreatedAndDone?: (targetAmount: number) => void;
}

export default function AssetSelectorWallet({
  onDashboard = false,
  onSelect,
  selectedSaleAsset,
  setSelectedSaleAsset,
  setDisabled,
  orderAmount,
  onChange,
  setParentRate,
  typeOfTrade = "Buy",
  onTargetCancel,
  pair,
  onTarget,
  onReject,
  onAccept,
  hotActionAsset,
  tradingAssets,
  setSelectedAsset,
  selectedAsset,
  amount,
  setAmount,
  isPostTrade,
  currentPairs,
  onPairSelect,
  zarBalance,
  fiatBalances,
  onOrderViewChange,
  onOrderCreatedAndDone,
}: AssetSelectorWalletProps) {
  const isBuy = typeOfTrade === "Buy";
  const isSell = typeOfTrade === "Sell";

  const [isCryptoInput, setIsCryptoInput] = useState<boolean>(false);
  const [tragetAmount, setTragetAmount] = useState<number | null>(
    orderAmount ?? null
  );
  const [rate, setRate] = useState<TradeRate | null>(null);
  const [convertedValue, setConvertedValue] = useState<string | number>(" ");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const [pairDialogOpen, setPairDialogOpen] = useState<boolean>(false);
  const [awaitingRate, setAwaitingRate] = useState<boolean>(false);
  const [showOrderCreation, setShowOrderCreation] = useState<boolean>(false);

  useEffect(() => {
    setFlag(false);
    setFlagMinAmount(false);
    setAmountFlag(false);
    setHighTarget(false);
    setAwaitingRate(false);
    setSelectedPair(null);
    setConvertedValue(" ");
    setDisabled?.(false);
    setIsCryptoInput(false);
    lastProcessedRef.current = null;
    lastUserEnteredAmountRef.current = null;
    lastRateRef.current = null;
    isSwitchingRef.current = false;
  }, [typeOfTrade, setDisabled]);

  const [flag, setFlag] = useState<boolean>(false);
  const [minAmount, setMinAmount] = useState<number | null>(null);
  const [flagMinAmount, setFlagMinAmount] = useState<boolean>(false);
  const [latestParams, setLatestParams] = useState<{
    pair: string;
    isPostTrade?: boolean;
  }>();
  const [highTarget, setHighTarget] = useState<boolean>(false);
  const [amountFlag, setAmountFlag] = useState<boolean>(false);
  const [targetAmountHolder, setTargetAmountHolder] = useState<number | null>(
    orderAmount ?? null
  );
  const [selectedPair, setSelectedPair] = useState<TradingPair | null>(null);
  const lastProcessedRef = useRef<{
    hotActionId: string | null;
    currentId: string | null;
    timestamp?: number | null;
  } | null>(null);
  const lastUserEnteredAmountRef = useRef<string | number | null>(null);
  const lastRateRef = useRef<number | null>(null);
  const isSwitchingRef = useRef<boolean>(false);

  const { toast } = useToast();
  const { showPersistentMessage } = useNotification();
  const formatter = new CurrencyFormatter();
  const isMobile = useIsMobile();

  const { data: catalogFromApi, isLoading: isLoadingCatalog } =
    useCryptoAssets();
  const resolvedCatalog = useMemo(() => {
    if (tradingAssets && tradingAssets.length > 0) {
      return tradingAssets;
    }
    return catalogFromApi ?? [];
  }, [tradingAssets, catalogFromApi]);
  const assetsToDisplay = resolvedCatalog;
  const isLoadingAssets = isLoadingCatalog && !tradingAssets?.length;

  const activeAsset = isBuy ? selectedAsset : selectedSaleAsset;
  const { data: assetPairs } = useFetchPairs(activeAsset?.assetId);

  const activePair = isBuy ? pair : selectedPair;

  const { data: tradeRate } = useTradeRate(
    activeAsset?.assetId,
    isBuy ? "buy" : "sell",
    activePair?.pair,
    isPostTrade
  );

  useEffect(() => {
    setRate(null);
    setConvertedValue(" ");
    setLatestParams(undefined);
  }, [activeAsset?.assetId, activePair?.pair]);

  useEffect(() => {
    if (!isSell) return;
    setSelectedPair(null);
  }, [isSell, activeAsset?.assetId]);

  useEffect(() => {
    if (!isSell || !selectedPair || !currentPairs) return;

    const isValidPair = currentPairs.some(
      (pairItem: TradingPair) => pairItem.pair === selectedPair.pair
    );

    if (!isValidPair) {
      setSelectedPair(null);
    }
  }, [isSell, selectedPair, currentPairs]);

  useEffect(() => {
    if (!isSell || !currentPairs || selectedPair || !activeAsset) return;
    try {
      const firstFiatPair = currentPairs.find(
        (pairItem: TradingPair) => pairItem.type === "Fiat"
      );
      if (firstFiatPair) {
        setSelectedPair(firstFiatPair);
        if (onPairSelect) {
          onPairSelect(firstFiatPair);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      toast({
        description: errorMessage,
        variant: "error",
      });
    }
  }, [isSell, currentPairs, selectedPair, activeAsset, onPairSelect, toast]);

  useEffect(() => {
    if (!activePair || !activeAsset) return;
    setAwaitingRate(true);
  }, [activePair, activeAsset, isSell]);

  useEffect(() => {
    if (!activePair || !activeAsset || !tradeRate) return;

    if (
      latestParams &&
      latestParams.pair === activePair.pair &&
      latestParams.isPostTrade === isPostTrade
    ) {
      setAwaitingRate(false);
      return;
    }
    setLatestParams({ pair: activePair.pair, isPostTrade });
    const setTradeRateData = () => {
      try {
        if (tradeRate) {
          setRate(tradeRate);
          setParentRate?.(tradeRate);
        }
      } catch (error) {
        if (error instanceof Error) {
          showPersistentMessage("error", error.message);
        } else {
          showPersistentMessage("error", "An error occurred");
        }
      } finally {
        setAwaitingRate(false);
      }
    };
    setTradeRateData();
  }, [
    tradeRate,
    activePair,
    activeAsset,
    latestParams,
    isPostTrade,
    setParentRate,
    showPersistentMessage,
  ]);

  const enrichedAssets = useMemo(() => {
    if (!assetsToDisplay) return undefined;

    return assetsToDisplay.map((asset: Asset) => {
      if (isSell && fiatBalances && fiatBalances.length > 0) {
        const fiatBalance = fiatBalances.find(
          (balance: { asset: string; valueInLocalCurrency: string }) =>
            balance.asset?.toLowerCase() === asset.asset?.toLowerCase()
        );

        if (fiatBalance?.valueInLocalCurrency) {
          return {
            ...asset,
            valueInLocalCurrency: fiatBalance.valueInLocalCurrency,
          } as Asset;
        }
      }

      return asset;
    });
  }, [assetsToDisplay, isSell, fiatBalances]);

  const filteredAssets = enrichedAssets?.filter((assets: Asset) => {
    const matchesSearch =
      assets?.asset?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      assets?.assetName?.toLowerCase().includes(searchTerm?.toLowerCase());

    if (!matchesSearch) return false;

    if (isSell && assets?.valueInLocalCurrency) {
      const numericValue = parseFloat(
        assets.valueInLocalCurrency.replace(/R|,|\s/g, "")
      );
      if (isNaN(numericValue) || numericValue < 1) {
        return false;
      }
    }

    return true;
  });

  const handleSelect = useCallback(
    (asset: Asset) => {
      const currentAsset = isBuy ? selectedAsset : selectedSaleAsset;

      if (asset.asset === currentAsset?.asset) {
        if (isBuy) {
          setSelectedAsset?.(asset);
        } else {
          setSelectedSaleAsset?.(asset);
        }
        setOpen(false);
        return;
      }
      if (asset.asset !== currentAsset?.asset) {
        setAwaitingRate(true);
        if (isBuy) {
          setSelectedAsset?.(asset);
        } else {
          setSelectedSaleAsset?.(asset);
        }
        if (onSelect) {
          onSelect(asset);
        }
        setOpen(false);
      }
    },
    [
      isBuy,
      selectedAsset,
      selectedSaleAsset,
      setSelectedAsset,
      setSelectedSaleAsset,
      onSelect,
    ]
  );

  useEffect(() => {
    if (!hotActionAsset) {
      lastProcessedRef.current = null;
      return;
    }

    const hotActionAssetId = hotActionAsset.assetId || hotActionAsset.asset;
    const currentAssetId = activeAsset?.assetId || activeAsset?.asset;
    const selectionTimestamp =
      typeof hotActionAsset._selectionTimestamp === "number"
        ? hotActionAsset._selectionTimestamp
        : null;

    if (currentAssetId === hotActionAssetId) {
      lastProcessedRef.current = {
        hotActionId: hotActionAssetId || null,
        currentId: currentAssetId || null,
        timestamp: selectionTimestamp || null,
      };
      return;
    }

    const lastProcessed = lastProcessedRef.current;
    const hotActionChanged =
      !lastProcessed || lastProcessed.hotActionId !== hotActionAssetId;
    const timestampChanged =
      selectionTimestamp && lastProcessed?.timestamp !== selectionTimestamp;
    const isFirstSelection = !currentAssetId;

    if (!hotActionChanged && !timestampChanged && !isFirstSelection) {
      return;
    }

    lastProcessedRef.current = {
      hotActionId: hotActionAssetId || null,
      currentId: currentAssetId || null,
      timestamp: selectionTimestamp || null,
    };
    handleSelect(hotActionAsset);
  }, [hotActionAsset, handleSelect, activeAsset]);

  const handlePairSelect = (pairItem: TradingPair): void => {
    if (isSell) {
      setSelectedPair(pairItem);
    }
    if (onPairSelect) {
      onPairSelect(pairItem);
    }
    setPairDialogOpen(false);
  };

  const showTargetExceedsRateError = () => {
    toast({
      description: isBuy
        ? "Target amount cannot be higher than default rate"
        : "Target amount cannot be lower than default rate",
      variant: "error",
    });
  };

  const handleTargetExceedsRate = (newTarget: number) => {
    setTargetAmountHolder(newTarget);
    setTragetAmount(null);
    setHighTarget(true);
    showTargetExceedsRateError();
  };

  const handleTargetWithinRate = (newTarget: number) => {
    setTargetAmountHolder(newTarget);
    setHighTarget(false);
  };

  const validateTargetAmount = (
    newTarget: number,
    currentRate: number
  ): void => {
    const isInvalid = isBuy ? newTarget > currentRate : newTarget < currentRate;

    if (isInvalid) {
      handleTargetExceedsRate(newTarget);
      return;
    }

    handleTargetWithinRate(newTarget);
  };

  const onTargetChange = (value: string) => {
    if (value === "") {
      setTargetAmountHolder(null);
      setHighTarget(false);
      return;
    }

    const newTarget = parseFloat(value);

    if (isNaN(newTarget)) {
      return;
    }

    const currentRate = rate?.rate ?? 0;
    validateTargetAmount(newTarget, currentRate);
  };

  const handleTargetDone = () => {
    const currentRate = rate?.rate ?? 0;
    const isValid = isBuy
      ? targetAmountHolder && targetAmountHolder <= currentRate
      : targetAmountHolder && targetAmountHolder >= currentRate;

    if (isValid && targetAmountHolder) {
      if (onOrderCreatedAndDone) {
        onOrderCreatedAndDone(targetAmountHolder);
      } else if (onTarget) {
        onTarget(targetAmountHolder);
      }
      setTragetAmount(targetAmountHolder);
      setHighTarget(false);
      setShowOrderCreation(false);
      onOrderViewChange?.(false);
    }
  };

  const takeOutInAmount = useCallback(
    (convertedValue: number, newAmount: number) => {
      if (onChange) {
        onChange(convertedValue, newAmount);
      }
    },
    [onChange]
  );

  const minAmountMoreThanMinimumLessThanMax = (rawValue: string): void => {
    setAmount?.(rawValue);
    if (onAccept) {
      onAccept();
    }
    setFlag(false);
    setFlagMinAmount(false);
    setDisabled?.(false);
  };

  const minAmountLessThanMinimum = (): void => {
    setFlagMinAmount(true);
    setDisabled?.(true);
    if (onReject) {
      onReject();
    }
  };

  const minAmountMoreThanMax = (): void => {
    setFlag(true);
    setDisabled?.(true);
    if (onReject) {
      onReject();
    }
  };

  const validateAmountAgainstLimits = (
    amount: number,
    minAmount: number,
    maxAvailable: number,
    rawValue: string
  ): void => {
    if (minAmount > 0 && !isNaN(minAmount)) {
      setMinAmount(minAmount);
    } else {
      setMinAmount(null);
    }

    const isWithinLimits = amount >= minAmount && amount <= maxAvailable;
    const isBelowMinimum = amount < minAmount;
    const isAboveMaximum = amount > maxAvailable;

    if (isWithinLimits) {
      minAmountMoreThanMinimumLessThanMax(rawValue);
      return;
    }

    if (isBelowMinimum && minAmount > 0) {
      minAmountLessThanMinimum();
      return;
    }

    if (isAboveMaximum) {
      minAmountMoreThanMax();
    }
  };

  const findAndValidatePairInfo = (
    newAmount: number,
    rawValue: string,
    inputType?: boolean
  ): void => {
    if (!assetPairs || !activePair?.pair) return;

    const matchingPairInfo = assetPairs.find(
      (info: PairInfo) => info.pair === activePair.pair
    );

    if (!matchingPairInfo) return;

    const currentInputType =
      inputType !== undefined ? inputType : isCryptoInput;

    const minAmountField = isBuy
      ? matchingPairInfo.minBuyAmountTail
      : matchingPairInfo.minSellAmountTail;

    let minAmountRaw = 0;
    if (minAmountField !== undefined && minAmountField !== null) {
      minAmountRaw =
        typeof minAmountField === "number"
          ? minAmountField
          : parseFloat(
              (minAmountField as { $numberDecimal?: string })?.$numberDecimal ??
                "0"
            );
    }

    if (!minAmountRaw || minAmountRaw <= 0 || isNaN(minAmountRaw)) {
      setMinAmount(null);
      const pairBalance = activePair.availableAmount ?? 0;
      const isZarPair = activePair.asset === "zar";
      const effectiveBalance =
        isBuy && isZarPair && pairBalance === 0 && zarBalance > 0
          ? zarBalance
          : pairBalance;

      if (currentInputType) {
        if (isBuy) {
          const maxAvailableInCrypto = effectiveBalance / (rate?.rate ?? 1);
          if (newAmount > maxAvailableInCrypto) {
            minAmountMoreThanMax();
          } else {
            minAmountMoreThanMinimumLessThanMax(rawValue);
          }
        } else {
          const maxAvailable = activeAsset?.availableAmount ?? 0;
          if (newAmount > maxAvailable) {
            minAmountMoreThanMax();
          } else {
            minAmountMoreThanMinimumLessThanMax(rawValue);
          }
        }
      } else {
        if (isBuy) {
          const maxAvailable = effectiveBalance;
          if (newAmount > maxAvailable) {
            minAmountMoreThanMax();
          } else {
            minAmountMoreThanMinimumLessThanMax(rawValue);
          }
        } else {
          const maxAvailableInFiat =
            (activeAsset?.availableAmount ?? 0) * (rate?.rate ?? 1);
          if (newAmount > maxAvailableInFiat) {
            minAmountMoreThanMax();
          } else {
            minAmountMoreThanMinimumLessThanMax(rawValue);
          }
        }
      }
      return;
    }

    const pairBalance = activePair.availableAmount ?? 0;
    const isZarPair = activePair.asset === "zar";
    const effectiveBalance =
      isBuy && isZarPair && pairBalance === 0 && zarBalance > 0
        ? zarBalance
        : pairBalance;

    if (currentInputType) {
      if (isBuy) {
        const minAmountInCrypto = minAmountRaw / (rate?.rate ?? 1);
        const maxAvailableInCrypto = effectiveBalance / (rate?.rate ?? 1);
        validateAmountAgainstLimits(
          newAmount,
          minAmountInCrypto,
          maxAvailableInCrypto,
          rawValue
        );
      } else {
        const minAmountInCrypto = minAmountRaw / (rate?.rate ?? 1);
        const maxAvailable = activeAsset?.availableAmount ?? 0;
        validateAmountAgainstLimits(
          newAmount,
          minAmountInCrypto,
          maxAvailable,
          rawValue
        );
      }
    } else {
      if (isBuy) {
        const maxAvailable = effectiveBalance;
        validateAmountAgainstLimits(
          newAmount,
          minAmountRaw,
          maxAvailable,
          rawValue
        );
      } else {
        const maxAvailableInFiat =
          (activeAsset?.availableAmount ?? 0) * (rate?.rate ?? 1);

        validateAmountAgainstLimits(
          newAmount,
          minAmountRaw,
          maxAvailableInFiat,
          rawValue
        );
      }
    }
  };

  const onTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;

    if (!/^\d*\.?\d*$/.test(rawValue)) return;

    setAmount?.(rawValue);
    const newAmount = parseFloat(rawValue);

    if (!newAmount || isNaN(newAmount)) {
      setAmountFlag(true);
      setFlagMinAmount(false);
      setFlag(false);
      setDisabled?.(true);
      lastUserEnteredAmountRef.current = rawValue;
      lastUserEnteredAmountRef.current = rawValue;
      return;
    }

    setAmountFlag(false);
    setDisabled?.(false);

    lastUserEnteredAmountRef.current = rawValue;

    if (rate?.rate) {
      let convertedValue: number;
      if (isCryptoInput) {
        convertedValue = newAmount * rate.rate;
        takeOutInAmount(
          isSell ? newAmount : convertedValue,
          isSell ? convertedValue : newAmount
        );
      } else {
        convertedValue = newAmount / rate.rate;
        takeOutInAmount(
          isSell ? convertedValue : newAmount,
          isSell ? newAmount : convertedValue
        );
      }
    }

    if (assetPairs) {
      findAndValidatePairInfo(newAmount, rawValue);
    }
  };

  useEffect(() => {
    if (isSwitchingRef.current) {
      isSwitchingRef.current = false;
      return;
    }

    const number = parseFloat(String(amount ?? 0));

    if (isNaN(number) || number === 0) {
      setConvertedValue(0);
      setDisabled?.(true);
      return;
    }

    if (!rate?.rate) {
      setConvertedValue(" ");
      return;
    }

    const isRateChange =
      lastRateRef.current !== null &&
      lastRateRef.current !== rate.rate &&
      lastUserEnteredAmountRef.current === amount;

    lastRateRef.current = rate.rate;

    const pairBalance = activePair?.availableAmount ?? 0;
    const isZarPair = activePair?.asset === "zar";
    const effectiveBalance =
      isBuy && isZarPair && pairBalance === 0 && zarBalance > 0
        ? zarBalance
        : pairBalance;

    const availableBalance = isBuy
      ? effectiveBalance
      : (activeAsset?.availableAmount ?? 0);

    if (!tragetAmount) {
      let convertedValue: number;
      let amountToCheck: number;

      if (isCryptoInput) {
        convertedValue = number * rate.rate;
        const pairAsset = activePair?.asset?.toLowerCase();
        const isTwoDecimalCurrency =
          pairAsset === "usdc" || pairAsset === "usdt" || pairAsset === "zar";
        const decimals = isTwoDecimalCurrency ? 2 : 6;
        setConvertedValue(truncateDecimals(convertedValue, decimals));

        amountToCheck = isBuy ? convertedValue : number;
      } else {
        convertedValue = number / rate.rate;
        const asset = activeAsset?.asset?.toLowerCase();
        const assetSymbol = activeAsset?.asset?.toUpperCase();
        const isTwoDecimalCurrency =
          asset === "usdc" || asset === "usdt" || asset === "zar";
        const decimals =
          assetSymbol === "BTC" ? 6 : isTwoDecimalCurrency ? 2 : 6;
        setConvertedValue(truncateDecimals(convertedValue, decimals));

        amountToCheck = isBuy ? number : convertedValue;
      }

      if (amountToCheck > availableBalance) {
        if (!isPostTrade) {
          setFlag(true);
          setDisabled?.(true);
          if (onReject) {
            onReject();
          }
        }
        if (isPostTrade) {
          setFlag(false);
          if (!flagMinAmount && !amountFlag) {
            setDisabled?.(false);
          }
          if (onAccept) {
            onAccept();
          }
        }
      }
      if (amountToCheck <= availableBalance) {
        setFlag(false);
        if (!flagMinAmount && !amountFlag) {
          setDisabled?.(false);
        }
        if (onAccept) {
          onAccept();
        }
        if (isRateChange) {
          const preservedAmount =
            typeof amount === "string"
              ? parseFloat(amount)
              : typeof amount === "number"
                ? amount
                : number;

          if (isBuy && !isCryptoInput) {
            const newConvertedValue = preservedAmount / rate.rate;
            takeOutInAmount(newConvertedValue, preservedAmount);
          } else if (isBuy && isCryptoInput) {
            const newConvertedValue = preservedAmount * rate.rate;
            takeOutInAmount(preservedAmount, newConvertedValue);
          } else if (isSell && !isCryptoInput) {
            const newConvertedValue = preservedAmount / rate.rate;
            takeOutInAmount(preservedAmount, newConvertedValue);
          } else if (isSell && isCryptoInput) {
            const newConvertedValue = preservedAmount * rate.rate;
            takeOutInAmount(newConvertedValue, preservedAmount);
          }
        } else {
          if (isCryptoInput) {
            takeOutInAmount(
              isBuy ? convertedValue : number,
              isBuy ? number : convertedValue
            );
          } else {
            takeOutInAmount(
              isBuy ? number : convertedValue,
              isBuy ? convertedValue : number
            );
          }
        }
      }
    }
    if (tragetAmount) {
      const convertedValue = isCryptoInput
        ? number * tragetAmount
        : number / tragetAmount;
      if (isCryptoInput) {
        const pairAsset = activePair?.asset?.toLowerCase();
        const isTwoDecimalCurrency =
          pairAsset === "usdc" || pairAsset === "usdt" || pairAsset === "zar";
        const decimals = isTwoDecimalCurrency ? 2 : 6;
        setConvertedValue(truncateDecimals(convertedValue, decimals));
      } else {
        const asset = activeAsset?.asset?.toLowerCase();
        const assetSymbol = activeAsset?.asset?.toUpperCase();
        const isTwoDecimalCurrency =
          asset === "usdc" || asset === "usdt" || asset === "zar";
        const decimals =
          assetSymbol === "BTC" ? 6 : isTwoDecimalCurrency ? 2 : 6;
        setConvertedValue(truncateDecimals(convertedValue, decimals));
      }
    }
  }, [
    amount,
    rate,
    tragetAmount,
    isPostTrade,
    isBuy,
    isCryptoInput,
    activeAsset?.availableAmount,
    activeAsset?.asset,
    activePair?.availableAmount,
    activePair?.asset,
    isSell,
    onAccept,
    onReject,
    setDisabled,
    zarBalance,
    takeOutInAmount,
    flagMinAmount,
    amountFlag,
  ]);

  const maxQuoteAmount = () => {
    let newAmountValue: number;
    let rawValue: string;

    if (isBuy) {
      const pairBalance = activePair?.availableAmount ?? 0;
      const isZarPair = activePair?.asset === "zar";
      const effectiveBalance =
        isZarPair && pairBalance === 0 && zarBalance > 0
          ? zarBalance
          : pairBalance;

      if (isCryptoInput) {
        newAmountValue = effectiveBalance / (rate?.rate ?? 1);
        if (isNaN(newAmountValue) || newAmountValue <= 0 || !rate?.rate) {
          return;
        }
        rawValue = newAmountValue.toString();
        setAmount?.(rawValue);
      } else {
        newAmountValue = effectiveBalance;
        if (isNaN(newAmountValue) || newAmountValue <= 0) {
          return;
        }
        rawValue = newAmountValue.toString();
        setAmount?.(rawValue);
      }
    } else {
      const maxCryptoAmount = activeAsset?.availableAmount ?? 0;

      if (isCryptoInput) {
        newAmountValue = maxCryptoAmount;
        if (isNaN(newAmountValue) || newAmountValue <= 0) {
          return;
        }
        rawValue = newAmountValue.toString();
        setAmount?.(rawValue);
      } else {
        newAmountValue = maxCryptoAmount * (rate?.rate ?? 1);
        if (isNaN(newAmountValue) || newAmountValue <= 0 || !rate?.rate) {
          return;
        }
        rawValue = newAmountValue.toString();
        setAmount?.(rawValue);
      }
    }

    if (!isNaN(newAmountValue) && assetPairs) {
      findAndValidatePairInfo(newAmountValue, rawValue);
    }
  };

  const switchConversion = () => {
    const currentAmount = amount ? Number(amount) : 0;
    const newIsCryptoInput = !isCryptoInput;

    if (currentAmount === 0 || isNaN(currentAmount)) {
      setIsCryptoInput(newIsCryptoInput);
      setConvertedValue(" ");
      return;
    }

    let currentConvertedNum = 0;
    if (
      convertedValue !== " " &&
      convertedValue !== "" &&
      convertedValue !== 0 &&
      convertedValue !== "0"
    ) {
      if (typeof convertedValue === "number") {
        currentConvertedNum = convertedValue;
      } else {
        const parsed = parseFloat(String(convertedValue));
        if (!isNaN(parsed) && parsed > 0) {
          currentConvertedNum = parsed;
        }
      }
    }

    if (currentConvertedNum === 0 || isNaN(currentConvertedNum)) {
      if (!rate?.rate) {
        setIsCryptoInput(newIsCryptoInput);
        setConvertedValue(" ");
        return;
      }

      if (newIsCryptoInput) {
        currentConvertedNum = currentAmount / rate.rate;
      } else {
        currentConvertedNum = currentAmount * rate.rate;
      }
    }

    const rawValue = currentConvertedNum.toString();

    isSwitchingRef.current = true;
    setAmount?.(rawValue);
    setConvertedValue(currentAmount);
    setIsCryptoInput(newIsCryptoInput);

    setTimeout(() => {
      isSwitchingRef.current = false;
    }, 100);

    if (assetPairs && activePair?.pair) {
      findAndValidatePairInfo(currentConvertedNum, rawValue, newIsCryptoInput);
    }
  };

  const handleCancel = () => {
    setTragetAmount(null);
    setTargetAmountHolder(null);
    setShowOrderCreation(false);
    onOrderViewChange?.(false);
  };

  const handleCloseOrderCreation = () => {
    setShowOrderCreation(false);
    setTargetAmountHolder(tragetAmount);
    onOrderViewChange?.(false);
  };

  const handleOpenOrderCreation = () => {
    if (!activeAsset) {
      toast({
        description: "Please select an asset first",
        variant: "error",
      });
      return;
    }
    setShowOrderCreation(true);
    onOrderViewChange?.(true);
  };

  const fiatLocalCurrencyBalance = useCallback(
    (selectedAsset?: Asset | null) => {
      if (!selectedAsset) return "R 0.00";
      const balance =
        fiatBalances.find(
          (balance: { asset: string; valueInLocalCurrency: string }) =>
            balance.asset === selectedAsset.asset
        )?.valueInLocalCurrency ?? "R 0.00";
      return formatFiatWithTwoDecimals(balance);
    },
    [fiatBalances]
  );

  return (
    <>
      {showOrderCreation ? (
        <OrderCreationCard
          isBuy={isBuy}
          activeAsset={activeAsset}
          activePair={activePair}
          rate={rate}
          targetAmountHolder={targetAmountHolder}
          highTarget={highTarget}
          onTargetChange={onTargetChange}
          onClose={handleCloseOrderCreation}
          onCancel={handleCancel}
          onDone={handleTargetDone}
        />
      ) : (
        <>
          <div className="flex flex-col gap-4">
            {isBuy && (
              <>
                <div className="space-y-2 col-span-1">
                  <label className="text-text-secondary text-sm">
                    Select asset
                  </label>
                  {isMobile ? (
                    <AssetSelectionDialog
                      selectedAsset={activeAsset}
                      searchTerm={searchTerm}
                      onSearchChange={setSearchTerm}
                      filteredAssets={filteredAssets}
                      onAssetSelect={handleSelect}
                      showBalance={false}
                      open={open}
                      onOpenChange={setOpen}
                      isLoading={isLoadingAssets}
                    />
                  ) : (
                    <AssetSelectionDropdown
                      selectedAsset={activeAsset}
                      searchTerm={searchTerm}
                      onSearchChange={setSearchTerm}
                      filteredAssets={filteredAssets}
                      onAssetSelect={handleSelect}
                      showBalance={false}
                      open={open}
                      onOpenChange={setOpen}
                      isLoading={isLoadingAssets}
                    />
                  )}
                </div>

                <TradePair
                  typeOfTrade={typeOfTrade}
                  currentPairs={currentPairs}
                  selectedAsset={selectedAsset}
                  onSelect={handlePairSelect}
                  setParentRate={setParentRate}
                  zarBalance={zarBalance}
                  fiatLocalCurrencyBalance={fiatLocalCurrencyBalance(
                    pair as Asset | null
                  )}
                  onDashboard={onDashboard}
                />
              </>
            )}

            {isSell && (
              <>
                <div className="flex flex-col md:flex-row gap-4 space-y-2 w-full">
                  <div
                    className={`space-y-2 ${onDashboard ? "md:w-[40%]" : "md:w-[30%]"} w-full`}
                  >
                    <label className="text-text-secondary text-sm">Asset</label>
                    {isMobile ? (
                      <AssetSelectionDialog
                        selectedAsset={activeAsset}
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        filteredAssets={filteredAssets}
                        onAssetSelect={handleSelect}
                        showBalance={true}
                        formatter={formatter}
                        open={open}
                        onOpenChange={setOpen}
                        isLoading={isLoadingAssets}
                      />
                    ) : (
                      <AssetSelectionDropdown
                        selectedAsset={activeAsset}
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        filteredAssets={filteredAssets}
                        onAssetSelect={handleSelect}
                        showBalance={true}
                        formatter={formatter}
                        open={open}
                        onOpenChange={setOpen}
                        isLoading={isLoadingAssets}
                        matchTriggerWidth={false}
                      />
                    )}
                  </div>

                  <div
                    className={`space-y-2 ${onDashboard ? "md:w-[60%]" : "md:w-[70%]"} w-full`}
                  >
                    <label className="text-text-secondary text-sm">
                      Available balance
                    </label>
                    <div className="py-3 md:py-4 px-4 md:px-6 rounded-2xl bg-gray-50 flex items-center h-[70px] md:h-[88px]">
                      <div className="text-lg md:text-xl font-semibold text-[#6D6584] flex justify-between items-center w-full gap-2 md:gap-0">
                        <p className="text-base md:text-lg">
                          {(() => {
                            const formatted =
                              selectedSaleAsset?.availableAmountFormatted ??
                              "0.00";
                            if (
                              selectedSaleAsset?.asset.toLowerCase() === "zar"
                            ) {
                              return formatted.replace(/,(\d{2})$/, ".$1");
                            }
                            return formatted;
                          })()}
                        </p>
                        {!onDashboard && (
                          <p className="text-sm md:text-base">
                            ≈ {fiatLocalCurrencyBalance(selectedSaleAsset)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 w-full mb-[9px]">
                  <label className="text-text-secondary text-sm">
                    Sell for
                  </label>
                  {isMobile ? (
                    <PairSelectionDialog
                      selectedPair={selectedPair}
                      selectedAsset={activeAsset}
                      currentPairs={currentPairs}
                      onPairSelect={handlePairSelect}
                      open={pairDialogOpen}
                      onOpenChange={setPairDialogOpen}
                      title="Sell for"
                      emptyStateMessage="Choose an asset to see receiving options"
                      loadingMessage="Loading receiving options..."
                    />
                  ) : (
                    <PairSelectionDropdown
                      selectedPair={selectedPair}
                      selectedAsset={activeAsset}
                      currentPairs={currentPairs}
                      onPairSelect={handlePairSelect}
                      open={pairDialogOpen}
                      onOpenChange={setPairDialogOpen}
                      emptyStateMessage="Choose an asset to see receiving options"
                      loadingMessage="Loading receiving options..."
                    />
                  )}
                </div>
              </>
            )}

            <div className="space-y-2 md:col-span-3">
              <label className="block text-[14px] text-[#6D6584] mb-2">
                Enter amount
              </label>
              <div className="space-y-2">
                <div
                  className={`relative flex items-center gap-4 rounded-2xl py-3 px-6 h-[70px] md:h-[88px] border transition-colors ${
                    flag || flagMinAmount || amountFlag
                      ? "border-2 border-rose-500"
                      : "border-[#CECBD7]"
                  }`}
                >
                  {!isCryptoInput ? (
                    <p className="text-[24px] font-semibold text-[#6D6584]">
                      {activePair?.asset.toUpperCase() ?? "R"}
                    </p>
                  ) : (
                    <p className="text-[24px] font-semibold text-[#6D6584]">
                      {activeAsset?.asset.toUpperCase()}
                    </p>
                  )}
                  <input
                    type="text"
                    value={amount ?? ""}
                    onChange={onTextChange}
                    className={`flex-1 w-[60%] text-lg font-semibold bg-transparent outline-none 
              focus-visible:ring-0 focus-visible:outline-none transition-colors duration-200 ease-in-out
                    ${flag || amountFlag ? "text-rose-500" : "text-purple-900"}`}
                    placeholder="0"
                  />
                  {(() => {
                    const pairBalance = activePair?.availableAmount ?? 0;
                    const isZarPair = activePair?.asset === "zar";
                    const effectiveBalance =
                      isBuy && isZarPair && pairBalance === 0 && zarBalance > 0
                        ? zarBalance
                        : pairBalance;

                    const hasBalance =
                      (isBuy && effectiveBalance > 0) ||
                      (isSell && (activeAsset?.availableAmount ?? 0) > 0);

                    return (
                      <>
                        <div className="text-text-secondary text-sm md:text-base">
                          ≈{" "}
                          {convertedValue === " " ||
                          isNaN(parseFloat(String(convertedValue)))
                            ? (() => {
                                if (isCryptoInput) {
                                  const pairAsset =
                                    activePair?.asset?.toLowerCase();
                                  const isTwoDecimalCurrency =
                                    pairAsset === "usdc" ||
                                    pairAsset === "usdt" ||
                                    pairAsset === "zar";
                                  const decimals = isTwoDecimalCurrency ? 2 : 6;
                                  return `0.${"0".repeat(decimals)}`;
                                }
                                const asset = activeAsset?.asset?.toLowerCase();
                                const assetSymbol =
                                  activeAsset?.asset?.toUpperCase();
                                const isTwoDecimalCurrency =
                                  asset === "usdc" ||
                                  asset === "usdt" ||
                                  asset === "zar";
                                const decimals =
                                  assetSymbol === "BTC"
                                    ? 6
                                    : isTwoDecimalCurrency
                                      ? 2
                                      : 6;
                                return `0.${"0".repeat(decimals)}`;
                              })()
                            : (() => {
                                if (isCryptoInput) {
                                  const pairAsset =
                                    activePair?.asset?.toLowerCase();
                                  const isTwoDecimalCurrency =
                                    pairAsset === "usdc" ||
                                    pairAsset === "usdt" ||
                                    pairAsset === "zar";
                                  const decimals = isTwoDecimalCurrency ? 2 : 6;
                                  return truncateDecimals(
                                    parseFloat(String(convertedValue)),
                                    decimals
                                  );
                                }
                                const asset = activeAsset?.asset?.toLowerCase();
                                const assetSymbol =
                                  activeAsset?.asset?.toUpperCase();
                                const isTwoDecimalCurrency =
                                  asset === "usdc" ||
                                  asset === "usdt" ||
                                  asset === "zar";
                                const decimals =
                                  assetSymbol === "BTC"
                                    ? 6
                                    : isTwoDecimalCurrency
                                      ? 2
                                      : 6;
                                return truncateDecimals(
                                  parseFloat(String(convertedValue)),
                                  decimals
                                );
                              })()}{" "}
                          {isCryptoInput
                            ? activePair?.asset.toUpperCase()
                            : activeAsset?.asset.toUpperCase()}{" "}
                        </div>
                        {hasBalance && (
                          <div className="flex items-center gap-2 justify-end sm:justify-start">
                            <Button
                              onClick={switchConversion}
                              variant="outline"
                              size="icon"
                              className="border border-neutral rounded-full h-8 w-8 md:h-auto md:w-auto"
                            >
                              <ArrowUpDown className="h-3 w-3 text-text-secondary" />
                            </Button>
                            <Button
                              onClick={maxQuoteAmount}
                              variant="outline"
                              className="border border-neutral rounded-full text-text-secondary px-4 md:px-6 text-sm md:text-base"
                            >
                              Max
                            </Button>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
              {flag && (
                <p className="mt-1 text-sm text-rose-500">
                  This amount exceeds your available balance
                </p>
              )}
              {flagMinAmount &&
                !flag &&
                minAmount !== null &&
                minAmount > 0 && (
                  <p className="mt-1 text-sm text-rose-500">
                    Minimum amount must be at least{" "}
                    {(() => {
                      const currency = isCryptoInput
                        ? activeAsset?.asset.toUpperCase()
                        : activePair?.asset.toUpperCase();
                      const asset = isCryptoInput
                        ? activeAsset?.asset?.toLowerCase()
                        : activePair?.asset?.toLowerCase();
                      const isZar =
                        !isCryptoInput && activePair?.asset === "zar";
                      const isTwoDecimalCurrency =
                        asset === "usdc" || asset === "usdt" || asset === "zar";

                      const formatted = isTwoDecimalCurrency
                        ? truncateDecimals(minAmount, 2)
                        : String(minAmount);

                      const prefix = isZar ? "R " : "";
                      return `${prefix}${formatted} ${currency}`;
                    })()}
                  </p>
                )}
              {amountFlag && !flag && !flagMinAmount && (
                <p className="mt-1 text-sm text-rose-500">
                  Please enter an amount
                </p>
              )}
            </div>
          </div>

          {!onDashboard && (
            <div className="flex flex-col sm:flex-row justify-center items-center text-center pt-4 pb-8 gap-3">
              <div className="flex justify-center">
                <Button
                  variant="ghost"
                  disabled={
                    awaitingRate ||
                    process.env.REACT_APP_BUY_SELL_ORDER_DISABLED === "true" ||
                    !amount ||
                    amount === "" ||
                    (typeof amount === "number"
                      ? amount <= 0
                      : isNaN(parseFloat(String(amount))) ||
                        parseFloat(String(amount)) <= 0)
                  }
                  onClick={handleOpenOrderCreation}
                  className="text-text-primary border border-neutral rounded-full p-4 md:p-6 text-sm md:text-base w-full sm:w-auto"
                >
                  Create {isBuy ? "buy" : "sell"} order
                </Button>
              </div>
              {tragetAmount && (
                <div className="flex justify-between gap-2">
                  <Button
                    variant="ghost"
                    onClick={handleOpenOrderCreation}
                    className="w-10 h-10 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-neutral hover:bg-gray-100"
                  >
                    <Pencil size={18} className="text-gray-700" />
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setTragetAmount(null);
                      setTargetAmountHolder(null);
                      onTargetCancel?.();
                    }}
                    className="w-10 h-10 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-neutral hover:bg-gray-100"
                  >
                    <Trash2 size={18} className="text-gray-700" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}
